import type { Patient, Reference } from '@medplum/fhirtypes'
import { MealPlatingQueue } from '@seen/core/models/meal-queues/MealPlatingQueue'
import { MealServingQueue } from '@seen/core/models/meal-queues/MealServingQueue'
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { type JSX, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

function MealQueueTable({
  queueType,
  onShowDetails,
}: {
  queueType: 'plating' | 'serving'
  onShowDetails: () => void
}): JSX.Element {
  const QueueClass =
    queueType === 'plating' ? MealPlatingQueue : MealServingQueue

  const { data: queues } = useSearch(QueueClass, QueueClass.currentQueueQuery())

  const queue = queues?.[0]

  const columns: ColumnDef<Reference<Patient>>[] = useMemo(
    () => [
      {
        accessorKey: 'participant',
        header: 'Participant',
        cell: ({ row }) => <ParticipantNameplate participant={row.original} />,
      },
      {
        id: 'position',
        header: 'Position',
        cell: ({ row }) => {
          const participants = queue?.participants ?? []
          const index = participants.findIndex(
            (p) => p.reference === row.original.reference
          )
          return <span className="text-2xl font-bold">{index + 1}</span>
        },
      },
    ],
    [queue?.participants]
  )

  const participants = queue?.participants ?? []
  const firstParticipant = participants[0]

  if (!queues) {
    return <Loader />
  }

  if (!queue || participants.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="mb-8 text-2xl text-gray-500">
          No participants in {queueType} queue
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-8 flex-1">
        <DataTable columns={columns} data={participants} />
      </div>

      {firstParticipant && (
        <div className="flex justify-center">
          <Button
            onClick={onShowDetails}
            size="lg"
            className="px-12 py-6 text-xl"
          >
            Show Details for Next Participant
          </Button>
        </div>
      )}
    </div>
  )
}

export function DailyMealQueuePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  let activeTab = searchParams.get('tab') ?? 'plating'
  if (!['plating', 'serving'].includes(activeTab)) {
    activeTab = 'plating'
  }

  const handleShowDetails = (): void => {
    if (activeTab === 'plating') {
      // Navigate to plating detail page
      window.location.href = '/meal-plating-detail'
    } else if (activeTab === 'serving') {
      // Navigate to serving detail page (to be implemented)
      window.location.href = '/meal-serving-detail'
    }
  }

  return (
    <PageLayout title="Meal Queue" className="h-screen">
      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setSearchParams((prev) => {
            prev.set('tab', value)
            return prev
          })
        }
        className="flex h-full grow flex-col"
      >
        <TabsList className="mb-6 w-fit text-xl">
          <TabsTrigger value="plating" className="px-8 py-4 text-xl">
            Meal Plating Queue
          </TabsTrigger>
          <TabsTrigger value="serving" className="px-8 py-4 text-xl">
            Meal Serving Queue
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plating" className="flex grow flex-col">
          <MealQueueTable
            queueType="plating"
            onShowDetails={handleShowDetails}
          />
        </TabsContent>

        <TabsContent value="serving" className="flex grow flex-col">
          <MealQueueTable
            queueType="serving"
            onShowDetails={handleShowDetails}
          />
        </TabsContent>
      </Tabs>
    </PageLayout>
  )
}
